'use client';
import React, { useState, useEffect } from 'react';
import { Shield, CheckCircle, Clock, AlertTriangle, XCircle, FileText, Upload, ChevronRight, RefreshCw, Eye, Info, ArrowRight, Calendar, User, ShieldCheck, ShieldAlert, ClipboardList, History, AlertCircle, Paperclip, Check } from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';
import MetricCard from '@/components/ui/MetricCard';
import Icon from '@/components/ui/AppIcon';



// ─── Types ────────────────────────────────────────────────────────────────────

type KYCStatus = 'approved' | 'pending' | 'under_review' | 'rejected' | 'incomplete' | 'expired';

interface KYCDocument {
  id: string;
  name: string;
  type: string;
  status: 'verified' | 'pending' | 'rejected' | 'required' | 'expired';
  uploadedDate?: string;
  expiryDate?: string;
  rejectionReason?: string;
  required: boolean;
}

interface NextStep {
  id: string;
  priority: 'critical' | 'warning' | 'info';
  title: string;
  description: string;
  action: string;
  actionHref: string;
  dueDate?: string;
}

interface SubmissionRecord {
  id: string;
  submittedAt: string;
  type: string;
  status: 'approved' | 'rejected' | 'pending' | 'under_review';
  reviewer?: string;
  notes?: string;
  documents: string[];
}

// ─── Mock Data ────────────────────────────────────────────────────────────────

const mockKYCApplication = {
  applicationId: 'KYC-2026-00847',
  status: 'under_review' as KYCStatus,
  investorType: 'Individual',
  submittedDate: '2026-08-15',
  lastUpdated: '2026-08-20',
  assignedReviewer: 'Compliance Team',
  estimatedCompletion: '2026-08-25',
  overallProgress: 72,
  investorClass: 'Sophisticated Investor (Pending)',
  amlStatus: 'screening' as const,
  suitabilityScore: null as number | null,
};

const mockDocuments: KYCDocument[] = [
  {
    id: 'doc-001',
    name: 'Passport (Primary ID)',
    type: 'Government-issued photo ID',
    status: 'verified',
    uploadedDate: '2026-08-15',
    expiryDate: '2030-04-22',
    required: true,
  },
  {
    id: 'doc-002',
    name: 'Proof of Address',
    type: 'Utility bill / Bank statement',
    status: 'pending',
    uploadedDate: '2026-08-15',
    required: true,
  },
  {
    id: 'doc-003',
    name: 'Source of Funds Declaration',
    type: 'Bank statement / Payslip',
    status: 'rejected',
    uploadedDate: '2026-08-15',
    rejectionReason: 'Document is older than 3 months. Please upload a recent statement.',
    required: true,
  },
  {
    id: 'doc-004',
    name: 'Tax Identification (TIN)',
    type: 'FATCA / CRS Self-Certification',
    status: 'required',
    required: true,
  },
  {
    id: 'doc-005',
    name: 'Suitability Questionnaire',
    type: 'Investor suitability assessment',
    status: 'verified',
    uploadedDate: '2026-08-15',
    required: true,
  },
  {
    id: 'doc-006',
    name: 'Liveness Check',
    type: 'Biometric verification',
    status: 'verified',
    uploadedDate: '2026-08-15',
    required: true,
  },
];

const mockNextSteps: NextStep[] = [
  {
    id: 'step-001',
    priority: 'critical',
    title: 'Re-upload Source of Funds',
    description: 'Your source of funds document was rejected. Please upload a bank statement or payslip dated within the last 3 months.',
    action: 'Upload Document',
    actionHref: '/kyc-submission',
    dueDate: '2026-08-28',
  },
  {
    id: 'step-002',
    priority: 'critical',
    title: 'Submit Tax Identification',
    description: 'Your FATCA/CRS self-certification form is missing. This is required to complete your KYC application.',
    action: 'Complete Now',
    actionHref: '/kyc-submission',
    dueDate: '2026-08-28',
  },
  {
    id: 'step-003',
    priority: 'warning',
    title: 'Compliance Review In Progress',
    description: 'Your application is currently being reviewed by our compliance team. You will be notified once the review is complete.',
    action: 'View Application',
    actionHref: '/kyc-status',
  },
  {
    id: 'step-004',
    priority: 'info',
    title: 'AML Screening Underway',
    description: 'Automated AML and sanctions screening is running. This typically completes within 24 hours.',
    action: 'Learn More',
    actionHref: '/kyc-status',
  },
];

const mockSubmissionHistory: SubmissionRecord[] = [
  {
    id: 'sub-003',
    submittedAt: '2026-08-15T09:32:00Z',
    type: 'Initial KYC Application',
    status: 'under_review',
    reviewer: 'Compliance Team',
    notes: 'Application received. Identity documents verified. Awaiting source of funds review.',
    documents: ['Passport', 'Proof of Address', 'Suitability Questionnaire', 'Liveness Check'],
  },
  {
    id: 'sub-002',
    submittedAt: '2026-06-10T14:15:00Z',
    type: 'Document Re-submission',
    status: 'rejected',
    reviewer: 'Sarah Mitchell',
    notes: 'Proof of address document was expired. Applicant notified to resubmit.',
    documents: ['Proof of Address (Expired)'],
  },
  {
    id: 'sub-001',
    submittedAt: '2026-06-01T11:00:00Z',
    type: 'Initial KYC Application',
    status: 'rejected',
    reviewer: 'James Okafor',
    notes: 'Incomplete application. Multiple required documents missing.',
    documents: ['Passport'],
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function docStatusConfig(status: KYCDocument['status']) {
  switch (status) {
    case 'verified': return { label: 'Verified', variant: 'approved' as const, icon: CheckCircle, color: 'text-positive' };
    case 'pending': return { label: 'Under Review', variant: 'pending' as const, icon: Clock, color: 'text-warning' };
    case 'rejected': return { label: 'Rejected', variant: 'rejected' as const, icon: XCircle, color: 'text-negative' };
    case 'required': return { label: 'Required', variant: 'flagged' as const, icon: AlertCircle, color: 'text-warning' };
    case 'expired': return { label: 'Expired', variant: 'rejected' as const, icon: AlertTriangle, color: 'text-negative' };
  }
}

function submissionStatusConfig(status: SubmissionRecord['status']) {
  switch (status) {
    case 'approved': return { label: 'Approved', variant: 'approved' as const };
    case 'rejected': return { label: 'Rejected', variant: 'rejected' as const };
    case 'pending': return { label: 'Pending', variant: 'pending' as const };
    case 'under_review': return { label: 'Under Review', variant: 'review' as const };
  }
}

function applicationStatusConfig(status: KYCStatus) {
  switch (status) {
    case 'approved': return { label: 'Approved', color: 'text-positive', bg: 'bg-positive/10 border-positive/20', icon: ShieldCheck };
    case 'pending': return { label: 'Pending Submission', color: 'text-warning', bg: 'bg-warning/10 border-warning/20', icon: Clock };
    case 'under_review': return { label: 'Under Review', color: 'text-info', bg: 'bg-info/10 border-info/20', icon: RefreshCw };
    case 'rejected': return { label: 'Rejected', color: 'text-negative', bg: 'bg-negative/10 border-negative/20', icon: XCircle };
    case 'incomplete': return { label: 'Incomplete', color: 'text-warning', bg: 'bg-warning/10 border-warning/20', icon: AlertTriangle };
    case 'expired': return { label: 'Expired', color: 'text-negative', bg: 'bg-negative/10 border-negative/20', icon: AlertTriangle };
  }
}

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatDateTime(dateStr: string) {
  return new Date(dateStr).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function ApplicationStatusBanner({ app }: { app: typeof mockKYCApplication }) {
  const cfg = applicationStatusConfig(app.status);
  const StatusIcon = cfg.icon;

  return (
    <div className={`rounded-xl border p-5 ${cfg.bg}`}>
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div className={`w-14 h-14 rounded-xl flex items-center justify-center bg-surface-2 border border-border flex-shrink-0`}>
          <StatusIcon size={26} className={cfg.color} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <h2 className="text-lg font-semibold text-foreground">KYC Application</h2>
            <span className={`text-xs font-mono-data px-2 py-0.5 rounded-full border ${cfg.bg} ${cfg.color}`}>{cfg.label}</span>
          </div>
          <p className="text-sm text-muted-foreground">
            Application ID: <span className="font-mono-data text-foreground">{app.applicationId}</span>
            <span className="mx-2 text-border">·</span>
            Submitted {formatDate(app.submittedDate)}
            <span className="mx-2 text-border">·</span>
            Last updated {formatDate(app.lastUpdated)}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1 flex-shrink-0">
          <span className="text-xs text-muted-foreground">Est. completion</span>
          <span className="text-sm font-medium text-foreground">{formatDate(app.estimatedCompletion)}</span>
        </div>
      </div>

      {/* Progress bar */}
      <div className="mt-4">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-xs text-muted-foreground">Application progress</span>
          <span className="text-xs font-mono-data text-foreground">{app.overallProgress}%</span>
        </div>
        <div className="h-2 bg-surface-2 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full bg-primary transition-all duration-700"
            style={{ width: `${app.overallProgress}%` }}
          />
        </div>
      </div>

      {/* Meta row */}
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Investor Type', value: app.investorType, icon: User },
          { label: 'Investor Class', value: app.investorClass, icon: Shield },
          { label: 'AML Status', value: app.amlStatus === 'screening' ? 'Screening…' : app.amlStatus, icon: ShieldAlert },
          { label: 'Suitability Score', value: app.suitabilityScore ? `${app.suitabilityScore}/100` : 'Pending', icon: ClipboardList },
        ].map(({ label, value, icon: Icon }) => (
          <div key={label} className="bg-surface-2/60 rounded-lg p-3 border border-border/50">
            <div className="flex items-center gap-1.5 mb-1">
              <Icon size={12} className="text-muted-foreground" />
              <span className="text-xs text-muted-foreground">{label}</span>
            </div>
            <p className="text-sm font-medium text-foreground truncate">{value}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function DocumentRow({ doc }: { doc: KYCDocument }) {
  const cfg = docStatusConfig(doc.status);
  const StatusIcon = cfg.icon;

  return (
    <div className="flex items-start gap-3 p-4 rounded-lg border border-border bg-surface-2 hover:bg-surface-3 transition-colors">
      <div className="w-9 h-9 rounded-lg bg-surface-3 border border-border flex items-center justify-center flex-shrink-0 mt-0.5">
        <FileText size={16} className="text-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2 mb-0.5">
          <p className="text-sm font-medium text-foreground">{doc.name}</p>
          {doc.required && (
            <span className="text-xs text-muted-foreground bg-surface-3 px-1.5 py-0.5 rounded border border-border">Required</span>
          )}
        </div>
        <p className="text-xs text-muted-foreground mb-1">{doc.type}</p>
        {doc.uploadedDate && (
          <p className="text-xs text-muted-foreground">Uploaded {formatDate(doc.uploadedDate)}</p>
        )}
        {doc.expiryDate && (
          <p className="text-xs text-muted-foreground">Expires {formatDate(doc.expiryDate)}</p>
        )}
        {doc.rejectionReason && (
          <div className="mt-2 flex items-start gap-1.5 text-xs text-negative bg-negative/5 border border-negative/20 rounded-md px-2.5 py-2">
            <AlertCircle size={12} className="flex-shrink-0 mt-0.5" />
            <span>{doc.rejectionReason}</span>
          </div>
        )}
      </div>
      <div className="flex flex-col items-end gap-2 flex-shrink-0">
        <div className={`flex items-center gap-1.5 text-xs font-medium ${cfg.color}`}>
          <StatusIcon size={13} />
          <span>{cfg.label}</span>
        </div>
        {(doc.status === 'rejected' || doc.status === 'required' || doc.status === 'expired') && (
          <button className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors">
            <Upload size={11} />
            <span>Upload</span>
          </button>
        )}
        {doc.status === 'verified' && (
          <button className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors">
            <Eye size={11} />
            <span>View</span>
          </button>
        )}
      </div>
    </div>
  );
}

function NextStepCard({ step }: { step: NextStep }) {
  const priorityConfig = {
    critical: { border: 'border-negative/30', bg: 'bg-negative/5', icon: AlertTriangle, iconColor: 'text-negative', badge: 'bg-negative/15 text-negative', label: 'Action Required' },
    warning: { border: 'border-warning/30', bg: 'bg-warning/5', icon: Clock, iconColor: 'text-warning', badge: 'bg-warning/15 text-warning', label: 'In Progress' },
    info: { border: 'border-info/30', bg: 'bg-info/5', icon: Info, iconColor: 'text-info', badge: 'bg-info/15 text-info', label: 'Info' },
  };
  const cfg = priorityConfig[step.priority];
  const StepIcon = cfg.icon;

  return (
    <div className={`rounded-xl border p-4 ${cfg.border} ${cfg.bg}`}>
      <div className="flex items-start gap-3">
        <div className={`w-8 h-8 rounded-lg bg-surface-2 border border-border flex items-center justify-center flex-shrink-0`}>
          <StepIcon size={15} className={cfg.iconColor} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <p className="text-sm font-semibold text-foreground">{step.title}</p>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cfg.badge}`}>{cfg.label}</span>
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed mb-3">{step.description}</p>
          <div className="flex items-center justify-between">
            {step.dueDate && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Calendar size={11} />
                <span>Due {formatDate(step.dueDate)}</span>
              </div>
            )}
            <a
              href={step.actionHref}
              className="flex items-center gap-1 text-xs font-medium text-primary hover:text-primary/80 transition-colors ml-auto"
            >
              {step.action}
              <ArrowRight size={11} />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function SubmissionHistoryRow({ record }: { record: SubmissionRecord }) {
  const [expanded, setExpanded] = useState(false);
  const cfg = submissionStatusConfig(record.status);

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 p-4 bg-surface-2 hover:bg-surface-3 transition-colors text-left"
      >
        <div className="w-8 h-8 rounded-lg bg-surface-3 border border-border flex items-center justify-center flex-shrink-0">
          <History size={14} className="text-muted-foreground" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground">{record.type}</p>
          <p className="text-xs text-muted-foreground">{formatDateTime(record.submittedAt)}</p>
        </div>
        <StatusBadge variant={cfg.variant} label={cfg.label} dot />
        <ChevronRight size={14} className={`text-muted-foreground transition-transform flex-shrink-0 ${expanded ? 'rotate-90' : ''}`} />
      </button>

      {expanded && (
        <div className="px-4 pb-4 pt-3 bg-surface-1 border-t border-border space-y-3">
          {record.reviewer && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <User size={12} />
              <span>Reviewed by <span className="text-foreground font-medium">{record.reviewer}</span></span>
            </div>
          )}
          {record.notes && (
            <div className="flex items-start gap-2 text-xs">
              <Info size={12} className="text-muted-foreground flex-shrink-0 mt-0.5" />
              <p className="text-muted-foreground leading-relaxed">{record.notes}</p>
            </div>
          )}
          <div>
            <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1.5">
              <Paperclip size={11} />
              Documents submitted ({record.documents.length})
            </p>
            <div className="flex flex-wrap gap-1.5">
              {record.documents.map((doc) => (
                <span key={doc} className="text-xs bg-surface-3 border border-border px-2 py-0.5 rounded-md text-foreground">
                  {doc}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function KYCStatusScreen() {
  const [activeTab, setActiveTab] = useState<'overview' | 'documents' | 'history'>('overview');
  const [loading, setLoading] = useState(true);
  const [application, setApplication] = useState(mockKYCApplication);
  const [documents, setDocuments] = useState<KYCDocument[]>([]);
  const [nextSteps, setNextSteps] = useState<NextStep[]>([]);
  const [history, setHistory] = useState<SubmissionRecord[]>([]);

  useEffect(() => {
    // Simulate loading; in production, fetch from Supabase
    const timer = setTimeout(() => {
      setApplication(mockKYCApplication);
      setDocuments(mockDocuments);
      setNextSteps(mockNextSteps);
      setHistory(mockSubmissionHistory);
      setLoading(false);
    }, 600);
    return () => clearTimeout(timer);
  }, []);

  const verifiedDocs = documents.filter(d => d.status === 'verified').length;
  const pendingDocs = documents.filter(d => d.status === 'pending').length;
  const rejectedDocs = documents.filter(d => d.status === 'rejected' || d.status === 'required').length;
  const criticalSteps = nextSteps.filter(s => s.priority === 'critical').length;

  const tabs = [
    { id: 'overview' as const, label: 'Overview & Next Steps', icon: ClipboardList },
    { id: 'documents' as const, label: 'Required Documents', icon: FileText, badge: rejectedDocs > 0 ? rejectedDocs : undefined },
    { id: 'history' as const, label: 'Submission History', icon: History },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center gap-3 text-muted-foreground">
          <RefreshCw size={18} className="animate-spin" />
          <span className="text-sm">Loading KYC application…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6 p-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">KYC Verification Status</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Track your identity verification progress and manage required documents
          </p>
        </div>
        <a
          href="/kyc-submission"
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary/90 transition-colors flex-shrink-0"
        >
          <Upload size={14} />
          Update Application
        </a>
      </div>

      {/* Status Banner */}
      <ApplicationStatusBanner app={application} />

      {/* KPI Row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <MetricCard
          label="Verified Docs"
          value={`${verifiedDocs}/${documents.length}`}
          trend="neutral"
          icon="check-circle"
        />
        <MetricCard
          label="Pending Review"
          value={pendingDocs}
          trend="neutral"
          icon="clock"
        />
        <MetricCard
          label="Action Required"
          value={rejectedDocs}
          trend={rejectedDocs > 0 ? 'down' : 'neutral'}
          icon="alert-triangle"
        />
        <MetricCard
          label="Critical Steps"
          value={criticalSteps}
          trend={criticalSteps > 0 ? 'down' : 'neutral'}
          icon="shield-alert"
        />
      </div>

      {/* Tabs */}
      <div className="border-b border-border">
        <div className="flex gap-0 overflow-x-auto">
          {tabs.map((tab) => {
            const TabIcon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <TabIcon size={15} />
                {tab.label}
                {tab.badge !== undefined && (
                  <span className="bg-negative/15 text-negative text-xs font-mono-data px-1.5 py-0.5 rounded-full">
                    {tab.badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-semibold text-foreground">Next Steps</h3>
            <span className="text-xs text-muted-foreground">{nextSteps.length} items</span>
          </div>
          {nextSteps.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <CheckCircle size={36} className="mx-auto mb-3 text-positive opacity-60" />
              <p className="text-sm font-medium">No pending actions</p>
              <p className="text-xs mt-1">Your application is progressing smoothly</p>
            </div>
          ) : (
            <div className="space-y-3">
              {nextSteps.map((step) => (
                <NextStepCard key={step.id} step={step} />
              ))}
            </div>
          )}

          {/* Verification Checklist */}
          <div className="mt-6">
            <h3 className="text-base font-semibold text-foreground mb-3">Verification Checklist</h3>
            <div className="rounded-xl border border-border bg-surface-2 divide-y divide-border overflow-hidden">
              {[
                { label: 'Identity Verification', status: 'passed', detail: 'Passport verified' },
                { label: 'Proof of Address', status: 'review', detail: 'Under compliance review' },
                { label: 'Source of Funds', status: 'failed', detail: 'Document rejected — resubmit required' },
                { label: 'AML / Sanctions Screening', status: 'review', detail: 'Automated screening in progress' },
                { label: 'FATCA / CRS Declaration', status: 'failed', detail: 'Not yet submitted' },
                { label: 'Suitability Assessment', status: 'passed', detail: 'Questionnaire completed' },
                { label: 'Liveness / Biometric Check', status: 'passed', detail: 'Biometric match confirmed' },
              ].map(({ label, status, detail }) => (
                <div key={label} className="flex items-center gap-3 px-4 py-3">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                    status === 'passed' ? 'bg-positive/15' : status === 'failed' ? 'bg-negative/15' : 'bg-warning/15'
                  }`}>
                    {status === 'passed' ? (
                      <Check size={12} className="text-positive" />
                    ) : status === 'failed' ? (
                      <XCircle size={12} className="text-negative" />
                    ) : (
                      <Clock size={12} className="text-warning" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground">{label}</p>
                    <p className="text-xs text-muted-foreground">{detail}</p>
                  </div>
                  <span className={`text-xs font-medium ${
                    status === 'passed' ? 'text-positive' : status === 'failed' ? 'text-negative' : 'text-warning'
                  }`}>
                    {status === 'passed' ? 'Passed' : status === 'failed' ? 'Action Needed' : 'In Review'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'documents' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-semibold text-foreground">Required Documents</h3>
            <span className="text-xs text-muted-foreground">{verifiedDocs} of {documents.length} verified</span>
          </div>
          <div className="space-y-3">
            {documents.map((doc) => (
              <DocumentRow key={doc.id} doc={doc} />
            ))}
          </div>
          <div className="mt-4 p-4 rounded-xl border border-info/20 bg-info/5 flex items-start gap-3">
            <Info size={15} className="text-info flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-foreground mb-0.5">Document Requirements</p>
              <p className="text-xs text-muted-foreground leading-relaxed">
                All documents must be clear, unedited, and dated within the last 3 months (where applicable).
                Accepted formats: PDF, JPG, PNG. Maximum file size: 10 MB per document.
              </p>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'history' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-semibold text-foreground">Submission History</h3>
            <span className="text-xs text-muted-foreground">{history.length} submissions</span>
          </div>
          {history.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <History size={36} className="mx-auto mb-3 opacity-40" />
              <p className="text-sm">No submission history yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {history.map((record) => (
                <SubmissionHistoryRow key={record.id} record={record} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
